import { PAGE_POSITIONS } from 'types';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';
import { fromProductListItem } from 'types/TrackableProduct';

import ExternalIcon from '~/assets/icons/ExternalIcon';
import ProductNotificationCount from '~/components/ProductNotificationCount';
import SaveProductButton from '~/components/SaveProductButton';
import ProductClickedEvent from '~/events/ProductClickedEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import { isUpcomingRelease } from '~/utils/products';

import AffiliateProductLink from './AffiliateProductLink';
import Image from './Image';
import Price from './Price';
import ProductNotificationButton from './ProductNotificationButton';
import StockStatus from './StockStatus';
import Tracked from './Tracked';

type Props = {
  allowNotifications: boolean;
  allowSaves: boolean;
  alwaysShowNotification?: boolean;
  cardSize: 'small' | 'large';
  className?: string;
  imageLoading?: 'lazy' | 'eager';
  overrideInteractionPagePosition?: boolean;
  padImage: boolean;
  pagePosition: PAGE_POSITIONS;
  showNotificationCount?: boolean;
};

interface ExternalProps extends Props {
  product: ProductListItemExternal;
}

interface InternalProps extends Props {
  name?: TrackedButtonClickName;
  onClick?: () => void;
  product: ProductListItemInternal;
}

const ExternalProductCard = ({
  allowNotifications,
  allowSaves,
  pagePosition,
  product,
  padImage,
  cardSize,
  className,
  imageLoading,
  alwaysShowNotification = false,
  showNotificationCount = false,
  overrideInteractionPagePosition = false
}: ExternalProps) => {
  return (
    <div className={`relative ${className}`}>
      <AffiliateProductLink
        affiliate={product.affiliate}
        buttonPosition={0}
        pagePosition={pagePosition}
        price={product.price}
        product={product}
        urlTemplates={product}
        variant="none"
        className="flex cursor-pointer flex-col justify-center gap-2 overflow-hidden"
      >
        <CardContents
          allowNotifications={allowNotifications}
          allowSaves={allowSaves}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          showNotificationCount={showNotificationCount}
        />
      </AffiliateProductLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        allowSaves={allowSaves}
        alwaysShowNotification={alwaysShowNotification}
        pagePosition={
          overrideInteractionPagePosition
            ? pagePosition
            : PAGE_POSITIONS.productCard
        }
      />
    </div>
  );
};

const InternalProductCard = ({
  allowNotifications,
  allowSaves,
  name = 'PRODUCT_CARD',
  pagePosition,
  product,
  padImage,
  cardSize,
  className,
  onClick,
  imageLoading,
  alwaysShowNotification = false,
  showNotificationCount = false,
  overrideInteractionPagePosition = false
}: InternalProps) => {
  const user = useUser();
  const { isBot } = useRootData();

  return (
    <div className={`relative ${className}`}>
      <Tracked.InternalLink
        data-cy="product-card"
        to={product.path}
        onClick={() => {
          new ProductClickedEvent({ isBot, product, user }).send();

          if (onClick) onClick();
        }}
        className="flex cursor-pointer flex-col justify-center gap-2 overflow-hidden"
        variant="none"
        name={name}
        pagePosition={pagePosition}
      >
        <CardContents
          allowNotifications={allowNotifications}
          allowSaves={allowSaves}
          cardSize={cardSize}
          padImage={padImage}
          pagePosition={pagePosition}
          product={product}
          imageLoading={imageLoading}
          showNotificationCount={showNotificationCount}
        />
      </Tracked.InternalLink>
      <CardInteraction
        product={product}
        allowNotifications={allowNotifications}
        allowSaves={allowSaves}
        alwaysShowNotification={alwaysShowNotification}
        pagePosition={
          overrideInteractionPagePosition
            ? pagePosition
            : PAGE_POSITIONS.productCard
        }
      />
    </div>
  );
};

function CardContents({
  product,
  cardSize,
  padImage,
  imageLoading,
  showNotificationCount
}: Props & { product: ProductListItemExternal | ProductListItemInternal }) {
  return (
    <>
      <div className="shadow-sm hover:opacity-80">
        <div className="absolute left-2 top-2 lg:left-4 lg:top-4">
          <StockStatus
            status={product.stock_status}
            releaseDate={product.date.released}
            releaseDateFormat={cardSize === 'large' ? 'long' : 'short'}
          />
        </div>
        <ProductImage
          alt={product.image?.alt}
          url={product.image?.url}
          padImage={padImage}
          cardSize={cardSize}
          imageLoading={imageLoading}
        />
        {showNotificationCount && product.weekly_notifications_count && (
          <ProductNotificationCount
            count={product.weekly_notifications_count}
          />
        )}
      </div>

      <div className="flex h-20 flex-col gap-2">
        <h4
          data-cy="product-name"
          className="text-left text-sm font-semibold line-clamp-2"
        >
          {product.name}
        </h4>
        <div className="flex flex-row gap-2 items-center">
          <Price
            regular={product.price.regular}
            sale={product.price.sale}
            currency={product.price.currency}
            className="gap-2 text-sm"
          />
          {product.type === 'external' && (
            <div className="flex flex-row gap-1">
              <ExternalIcon />
              <h4 className="text-sm text-grey-hide">
                {product.affiliate.name}
              </h4>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const ProductImage = ({
  alt,
  url,
  padImage,
  cardSize,
  imageLoading
}: {
  alt?: string;
  cardSize: 'small' | 'large';
  imageLoading: Props['imageLoading'];
  padImage: boolean;
  url?: string;
}) => {
  if (!url) return <SquareImageSkeleton />;

  const imageSize = cardSize === 'large' ? 768 : 474;

  return (
    <SquareImageSkeleton>
      <Image
        alt={alt}
        src={url}
        height={imageSize}
        width={imageSize}
        pad={padImage}
        quality={100}
        loading={imageLoading}
        responsiveOptions={{
          lg: { height: 400, pad: padImage, width: 400 },
          md: { height: 380, pad: padImage, width: 380 },
          sm: { height: 300, pad: padImage, width: 300 },
          xl: { height: 400, pad: padImage, width: 400 }
        }}
      />
    </SquareImageSkeleton>
  );
};

const SquareImageSkeleton = ({ children }: { children?: JSX.Element }) => {
  return (
    <div className="flex aspect-square w-full items-center justify-center bg-white">
      {children}
    </div>
  );
};

const CardInteraction = ({
  product,
  allowNotifications,
  allowSaves,
  alwaysShowNotification,
  pagePosition
}: {
  allowNotifications: boolean;
  allowSaves: boolean;
  alwaysShowNotification: boolean;
  pagePosition: PAGE_POSITIONS;
  product: ProductListItem;
}) => {
  const notificationsData = {
    id: product.id,
    image: product.image,
    legacyId: product.legacy_id,
    name: product.name,
    price: product.price.sale ?? product.price.regular,
    queryID: product.queryID
  };

  if (
    alwaysShowNotification ||
    (isUpcomingRelease(product.date.released, product.stock_status) &&
      allowNotifications)
  ) {
    return (
      <div className="absolute right-2 top-[12px] lg:right-4 lg:top-[20px]">
        <ProductNotificationButton
          product={notificationsData}
          pagePosition={pagePosition}
          buttonStyle="compact"
        />
      </div>
    );
  }

  if (allowSaves)
    return (
      <div className="absolute right-2 top-[12px] lg:right-4 lg:top-[20px]">
        <SaveProductButton
          product={fromProductListItem(product)}
          buttonStyle="compact"
        />
      </div>
    );

  return null;
};

export default {
  External: ExternalProductCard,
  Internal: InternalProductCard
};
